/**
 * Microphone Permission Utilities
 *
 * Reusable permission checking and error handling for audio features.
 * Used by idle stream and future recording system.
 */

import { logger } from '@/utils/logger/logger';

import { desktopAudioRecorderBridge } from './index';
import type { PermissionStatus } from './types';

/** Permission error messages */
export const PERMISSION_MESSAGES = {
  ERROR: 'Failed to check microphone permission',
} as const;

export interface SilentPermissionResult {
  /** True only when the OS has actually granted access — `NotDetermined` is not granted */
  granted: boolean;
  /** Raw OS status, so callers can tell "denied" apart from "never asked" */
  status: PermissionStatus;
  /**
   * Set only when something is genuinely wrong — access denied, or the status
   * could not be read. Never set for `NotDetermined`, which is a normal
   * fresh-install state rather than a failure.
   */
  error?: string;
}

/**
 * Check microphone permission silently — no alert, and no OS permission prompt.
 *
 * Use this for background checks where the user shouldn't be interrupted.
 * `NotDetermined` reports `granted: false`: acting on a "never asked" status opens the
 * microphone, and that is what raises the OS prompt out of nowhere on a fresh install.
 * Callers get `status` so they can treat "never asked" (stay quiet, wait for a
 * user-initiated flow to ask) differently from "denied" (a real error worth surfacing).
 */
export async function checkMicrophonePermissionSilent(): Promise<SilentPermissionResult> {
  try {
    const status = await desktopAudioRecorderBridge.checkMicrophonePermissionStatus();

    switch (status) {
      case 'Granted':
        return { granted: true, status };
      case 'NotDetermined':
        // Deliberately no `error`: never having been asked is not a failure, and a
        // fresh install is the routine case. An error string here would trip
        // error-monitoring middleware that inspects a non-null `error`, and any
        // caller following the `!granted -> surface the error` shape would show it
        // to the user. Callers separate this from a denial via `status`.
        return { granted: false, status };
      case 'Denied':
        return { granted: false, status, error: 'Microphone permission denied' };
      default:
        // `status` is typed as a closed union, but it crosses the IPC boundary at
        // runtime — an added Rust variant would arrive here unrecognised. Fail closed
        // rather than fall through to the "never asked" path, which would silently
        // keep the microphone shut with a misleading reason.
        logger.error('[AudioRecorder] Unrecognised permission status:', status);
        return {
          granted: false,
          status: 'Denied',
          error: PERMISSION_MESSAGES.ERROR,
        };
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : PERMISSION_MESSAGES.ERROR;
    logger.error('[AudioRecorder] Permission check failed:', message);
    // A failed read is not a "never asked" state — report it as denied so callers
    // fail closed rather than silently waiting for a prompt that will never come.
    return { granted: false, status: 'Denied', error: message };
  }
}
